import { PlatformEnvData, PlatformLogger } from '@wix/thunderbolt-symbols'
import { IComponentSDKLoader } from './platform'
import { ComponentsRegistryError, ComponentsRegistryErrorTypes } from './errors'

export const getComponentsSDKLoader = async ({
	platformEnvData,
	loadModule,
	logger,
	componentSdksUrl,
}: {
	platformEnvData: PlatformEnvData
	loadModule: <T>(url: string, scriptName: string) => Promise<T>
	logger: PlatformLogger
	componentSdksUrl: string | null
}): Promise<IComponentSDKLoader> => {
	/**
	 * `platformEnvData.componentsRegistry` exists only when `specs.thunderbolt.componentsRegistry` experiment is enabled
	 *
	 * Once registry experiment is merged, remove `if`
	 * https://jira.wixpress.com/browse/WCR-365
	 */
	if (platformEnvData.componentsRegistry) {
		const { createComponentsRegistryPlatform } = await import(
			'./platform' /* webpackChunkName: "componentsRegistryPlatform" */
		)

		const librariesTopology = platformEnvData.componentsRegistry.librariesTopology
		const mode = platformEnvData.componentsRegistry.mode

		try {
			const componentsRegistryPlatform = await createComponentsRegistryPlatform({
				runtime: platformEnvData.componentsRegistry.runtime,
				librariesTopology,
				mode,
			})

			return componentsRegistryPlatform.getComponentsSDKsLoader()
		} catch (e) {
			logger.captureError(
				new ComponentsRegistryError(
					'could not create platform components registry',
					ComponentsRegistryErrorTypes.INITIALIZATION_ERROR
				),
				{
					groupErrorsBy: 'values',
					tags: { method: 'getComponentsSDKLoader' },
					extra: { librariesTopology, mode, error: e },
				}
			)

			return {
				sdkTypeToComponentTypes: {},
				loadComponentSdks: () => {
					throw new ComponentsRegistryError(
						'could not load components. platform components registry was initialized with error',
						ComponentsRegistryErrorTypes.COMPONENT_LOADING_ERROR
					)
				},
			}
		}
	} else if (componentSdksUrl) {
		try {
			return loadModule<IComponentSDKLoader>(componentSdksUrl, 'componentSdks')
		} catch (e) {
			logger.captureError(
				new ComponentsRegistryError(
					'could not load component SDKs loader',
					ComponentsRegistryErrorTypes.INITIALIZATION_ERROR
				),
				{
					groupErrorsBy: 'values',
					tags: { method: 'getComponentsSDKLoader' },
					extra: { componentSdksUrl, error: e },
				}
			)

			return {
				sdkTypeToComponentTypes: {},
				loadComponentSdks: () => {
					throw new ComponentsRegistryError(
						'could not load components. component SDKs loader was not loaded',
						ComponentsRegistryErrorTypes.COMPONENT_LOADING_ERROR
					)
				},
			}
		}
	} else {
		throw new ComponentsRegistryError(
			'It is not possible to crete Components SDK loader. Registry experiment is off and `componentSdksUrl` was not provided',
			ComponentsRegistryErrorTypes.INVALID_ARGUMENTS
		)
	}
}
